import type { Experience } from '@buakei/types';
import type { ReactNode } from 'react';
import { Navigate, useRoutes, type RouteObject } from 'react-router';
import { RequireExperience, RequireSession } from '@/auth/guards';
import { EditorialQueuePage, SourceRegistryPage } from '@/pages/editorial/EditorialPages';
import { ReviewPage } from '@/pages/editorial/LiveEditorial';
import { QualityPage, QualityRunPage } from '@/pages/editorial/QualityPages';
import { DoctrinesPage, ReceivedLawPage } from '@/pages/editorial/RegisterPages';
import { MemoPage, MemosPage } from '@/pages/professional/MemoPages';
import {
  CaseBriefPage,
  LearningDashboard,
  QuizzesPage,
  SubjectsPage,
  TutorPage,
} from '@/pages/learning/LearningPages';
import { AccessAdminPage } from '@/pages/operations/AccessAdminPage';
import { ActivityPage } from '@/pages/operations/ActivityPage';
import { FeedbackPage, SupportInboxPage } from '@/pages/support/SupportPages';
import { YourDataPage } from '@/pages/support/YourDataPage';
import { CasePage, CasesPage } from '@/pages/library/CasePages';
import { CiteCheckPage } from '@/pages/library/CiteCheckPage';
import { LibraryActPage, LibraryAskPage, LibraryPage } from '@/pages/library/LibraryPages';
import { OperationsPage } from '@/pages/operations/OperationsPage';
import { DraftingHome, DraftPage, MatterPage, NewMatterPage } from '@/pages/drafting/DraftingPages';
import { DocumentsPage, PlannedModulePage } from '@/pages/professional/GatedPages';
import { ProfessionalDashboard } from '@/pages/professional/ProfessionalDashboard';
import { ResearchWorkbench } from '@/pages/professional/ResearchWorkbench';
import { PublicLayout } from '@/pages/public/PublicLayout';
import { PricingPage } from '@/pages/public/PricingPage';
import { SignUpPage } from '@/pages/public/SignUpPage';
import { HomePage, NotFoundPage, SecurityPage } from '@/pages/public/PublicPages';
import {
  AccountPage,
  OnboardingPage,
  SignInCompletePage,
  SignInPage,
} from '@/pages/public/SignInPages';
import { AppShell } from '@/shell/AppShell';

const gate = (experience: Experience, element: ReactNode) => (
  <RequireExperience experience={experience}>{element}</RequireExperience>
);

/**
 * Route table. Guards here shape the user experience only; they are not authorisation.
 * Server-side policy (packages/auth) is the control that protects data from Milestone 2.
 */
export const routes: RouteObject[] = [
  {
    element: <PublicLayout />,
    children: [
      { path: '/', element: <HomePage /> },
      { path: '/pricing', element: <PricingPage /> },
      { path: '/security', element: <SecurityPage /> },
      { path: '/sign-in', element: <SignInPage /> },
      { path: '/sign-in/complete', element: <SignInCompletePage /> },
      { path: '/sign-up', element: <SignUpPage /> },
      { path: '/account', element: <AccountPage /> },
      { path: '/onboarding', element: <OnboardingPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  {
    element: (
      <RequireSession>
        <AppShell />
      </RequireSession>
    ),
    children: [
      { path: '/professional', element: gate('professional', <ProfessionalDashboard />) },
      { path: '/professional/research', element: gate('professional', <ResearchWorkbench />) },
      { path: '/professional/memos', element: gate('professional', <MemosPage />) },
      { path: '/professional/memos/:memoId', element: gate('professional', <MemoPage />) },
      {
        path: '/professional/drafting',
        element: gate('professional', <DraftingHome audience="professional" />),
      },
      {
        path: '/professional/drafting/new',
        element: gate('professional', <NewMatterPage audience="professional" />),
      },
      {
        path: '/professional/drafting/:matterId',
        element: gate('professional', <MatterPage audience="professional" />),
      },
      {
        path: '/professional/drafting/:matterId/drafts/:draftId',
        element: gate('professional', <DraftPage audience="professional" />),
      },
      {
        path: '/learning/drafting',
        element: gate('learning', <DraftingHome audience="learning" />),
      },
      {
        path: '/learning/drafting/new',
        element: gate('learning', <NewMatterPage audience="learning" />),
      },
      {
        path: '/learning/drafting/:matterId',
        element: gate('learning', <MatterPage audience="learning" />),
      },
      {
        path: '/learning/drafting/:matterId/drafts/:draftId',
        element: gate('learning', <DraftPage audience="learning" />),
      },
      { path: '/professional/documents', element: gate('professional', <DocumentsPage />) },
      {
        path: '/professional/matters',
        element: gate('professional', <PlannedModulePage module="matters" />),
      },
      {
        path: '/professional/cases',
        element: gate('professional', <PlannedModulePage module="cases" />),
      },
      { path: '/learning', element: gate('learning', <LearningDashboard />) },
      { path: '/learning/tutor', element: gate('learning', <TutorPage />) },
      { path: '/learning/subjects', element: gate('learning', <SubjectsPage />) },
      { path: '/learning/cases', element: gate('learning', <CaseBriefPage />) },
      { path: '/learning/quizzes', element: gate('learning', <QuizzesPage />) },
      { path: '/editorial', element: gate('editorial', <EditorialQueuePage />) },
      { path: '/editorial/sources', element: gate('editorial', <SourceRegistryPage />) },
      { path: '/editorial/review/:versionId', element: gate('editorial', <ReviewPage />) },
      { path: '/editorial/quality', element: gate('editorial', <QualityPage />) },
      { path: '/editorial/received-law', element: gate('editorial', <ReceivedLawPage />) },
      { path: '/editorial/doctrines', element: gate('editorial', <DoctrinesPage />) },
      { path: '/editorial/quality/:runId', element: gate('editorial', <QualityRunPage />) },
      { path: '/operations', element: gate('operations', <OperationsPage />) },
      { path: '/operations/activity', element: gate('operations', <ActivityPage />) },
      { path: '/operations/access', element: gate('operations', <AccessAdminPage />) },
      { path: '/operations/support', element: gate('operations', <SupportInboxPage />) },
      // Any signed-in account can write to the team (ADR-030).
      { path: '/feedback', element: <FeedbackPage /> },
      { path: '/your-data', element: <YourDataPage /> },
      // Shared by every signed-in account, whatever its workspaces (ADR-016). The pages and
      // the Worker require a verified email.
      { path: '/library', element: <LibraryPage /> },
      { path: '/library/ask', element: <LibraryAskPage /> },
      { path: '/library/cite-check', element: <CiteCheckPage /> },
      { path: '/library/cases', element: <CasesPage /> },
      { path: '/library/cases/:versionId', element: <CasePage /> },
      { path: '/library/:versionId', element: <LibraryActPage /> },
      { path: '/operations/library', element: <Navigate to="/library" replace /> },
    ],
  },
];

export function AppRoutes() {
  return useRoutes(routes);
}
